// ─── General helper utilities ──────────────────────────────────────────────────

import { format, isValid, parseISO } from 'date-fns';

/** Merge class names, filtering falsy values */
export function cn(...classes: (string | undefined | null | false | 0 | 0n)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Parse either a yyyy-MM-dd date or a full ISO datetime; tolerates the trailing-Z
// variant the API uses.
function parseDateLike(input: string): Date {
  return input.length === 10 ? parseISO(`${input}T00:00:00`) : parseISO(input);
}

/** Format an ISO date string to a human-readable format */
export function formatDate(dateStr?: string | null, pattern = 'dd MMM yyyy'): string {
  if (!dateStr) return '';
  const date = parseDateLike(dateStr);
  return isValid(date) ? format(date, pattern) : dateStr;
}

/** Format an ISO date-time string as a date + time */
export function formatDateTime(dateStr?: string | null, pattern = 'dd MMM yyyy, HH:mm'): string {
  if (!dateStr) return '';
  const date = parseDateLike(dateStr);
  return isValid(date) ? format(date, pattern) : dateStr;
}

/** Format an ISO date-time string as just a time */
export function formatTime(dateStr?: string | null, pattern = 'HH:mm'): string {
  if (!dateStr) return '';
  const date = parseDateLike(dateStr);
  return isValid(date) ? format(date, pattern) : dateStr;
}

/** Today's date as yyyy-MM-dd (ISO date, no time) */
export function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/** Truncate a string to a max length with ellipsis */
export function truncate(str: string, maxLength: number): string {
  return str.length > maxLength ? `${str.slice(0, maxLength)}…` : str;
}

/** Capitalise the first letter of a string */
export function capitalise(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Convert a file size in bytes to a human-readable string */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Mask a bank account number, showing only last 4 digits */
export function maskAccountNumber(account: string): string {
  return account.replace(/.(?=.{4})/g, '•');
}

/** Mask a sort code */
export function maskSortCode(sortCode: string): string {
  return sortCode.replace(/\d/g, '•').replace(/•{2}(?=•)/g, '••-');
}

/** Generate a simple unique ID */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Delay execution for a given number of milliseconds (for mock API) */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Deep clone a plain object */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/** Check if a value is empty (null, undefined, empty string, empty array) */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Get initials from a full name */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/** Sanitise a string to prevent XSS */
export function sanitise(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
